/**
 * Renders the mobile version of the checkout page.
 *
 * @returns JSX.Element
 */
import React from "react";
import { Box } from "@mui/material";
import {
  CustomAnimation,
  FlightItinerarySummaryAccordion,
  useConfig,
} from "@thekilroygroup/shared-ui";

/**
 * Components
 */
import PaymentMethodSelector from "components/Checkout/PaymentMethodSelector/PaymentMethodSelector";
import ContactInformation from "components/Checkout/ContactInformation/ContactInformation";
import TravelerInformation from "components/Checkout/TravelerInformation/TravelerInformation";
import AdditionalProducts from "components/Checkout/AdditionalProducts/AdditionalProducts";

/**
 * Redux
 */
import {
  setIsValid,
  goToStep,
  checkoutStepsSelector,
} from "redux/checkoutSteps";
import { useDispatch, useSelector } from "react-redux";
import { itinerarySelector } from "redux/selectedItinerary";
import { useTranslations } from "hooks/useTranslations";

/**
 * Utils
 */
import { getFlightCardTranslations } from "utils/translationsUtils";
import { getAirlineNames, getAirportName } from "utils/flightDetailApiUtils";
import { IAppConfigObject } from "appConfig.interfaces";
import { BrandedFares } from "../BrandedFares/BrandedFares";

export const CheckoutMobile = () => {
  /**
   * Translations
   */
  const {
    translations,
    translations: { flightSummary },
  } = useTranslations();
  const {
    locale,
    appConfig: { SHARED_ASSETS_BASE_URL },
  } = useConfig<IAppConfigObject>();

  const flightCardTranslations = getFlightCardTranslations(translations);

  /**
   * Redux
   */
  const dispatch = useDispatch();
  const checkoutSteps = useSelector(checkoutStepsSelector);

  /**
   * Set valid state
   * @param key
   * @param isValid
   */
  const setValidState = (key: string, isValid: boolean): void => {
    dispatch(setIsValid({ key: key, valid: isValid }));
  };

  /**
   * Handle proceed
   * @param index
   */
  const handleProceed = (index: number): void => {
    dispatch(goToStep(index));
    window.scrollTo(0, 0);
  };

  const itinerary = useSelector(itinerarySelector);
  return (
    <>
      {checkoutSteps.currentStep == 0 && (
        <CustomAnimation in={true} type={"fade"} timeout={250}>
          <Box sx={{ px: 2, pt: 2 }}>
            <BrandedFares
              handleProceed={() => handleProceed(1)}
              valid={checkoutSteps.steps.brandedFares.valid}
            />
          </Box>
        </CustomAnimation>
      )}
      {checkoutSteps.currentStep == 1 && (
        <CustomAnimation in={true} type={"fade"} timeout={250}>
          <Box sx={{ px: 2, pt: 2 }}>
            <FlightItinerarySummaryAccordion
              {...flightCardTranslations}
              airlineLogosPath={`${SHARED_ASSETS_BASE_URL}/airline-logos`}
              itinerary={itinerary}
              getAirportName={getAirportName}
              title={flightSummary.details}
              locale={locale}
              defaultExpanded={false}
              getAirlineNames={getAirlineNames}
              flightItinerarySummaryTranslations={flightSummary}
              detailsAccordion={true}
            />
          </Box>
          <Box>
            <ContactInformation
              validateHandler={(isValid: boolean) =>
                setValidState("contactInformation", isValid)
              }
              handleProceed={() => handleProceed(2)}
              initialValidState={checkoutSteps.steps.contactInformation.valid}
            />
          </Box>
        </CustomAnimation>
      )}
      {checkoutSteps.currentStep == 2 && (
        <CustomAnimation in={true} type={"fade"} timeout={250}>
          <Box>
            <TravelerInformation
              validateHandler={(isValid: boolean) =>
                setValidState("travelerInformation", isValid)
              }
              handleProceed={() => handleProceed(3)}
              initialValidState={checkoutSteps.steps.travelerInformation.valid}
            />
          </Box>
        </CustomAnimation>
      )}
      {checkoutSteps.currentStep == 3 && (
        <CustomAnimation in={true} type={"fade"} timeout={250}>
          <Box>
            <AdditionalProducts
              validateHandler={(isValid: boolean) =>
                setValidState("additionalProducts", isValid)
              }
              handleProceed={() => handleProceed(4)}
              initialValidState={checkoutSteps.steps.additionalProducts.valid}
            />
          </Box>
        </CustomAnimation>
      )}
      {checkoutSteps.currentStep == 4 && (
        <CustomAnimation in={true} type={"fade"} timeout={250}>
          <Box>
            <PaymentMethodSelector
              validateHandler={(isValid: boolean) =>
                setValidState("paymentMethod", isValid)
              }
            />
          </Box>
        </CustomAnimation>
      )}
    </>
  );
};
